import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/authSlice';
import { RootState } from '../../store/store';
import {
  LayoutDashboard, Package, ShoppingCart, BarChart2, Warehouse,
  PieChart, User, LogOut, Stethoscope, ChevronRight, Truck, RotateCcw
} from 'lucide-react';
import Swal from 'sweetalert2';
import { useEffect, useState } from 'react';
import { sellerAPI, authAPI } from '../../api';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/orders', icon: ShoppingCart, label: 'Orders' },
  // { to: '/pickup', icon: Truck, label: 'Pick Up' },
  { to: '/inventory', icon: Warehouse, label: 'Inventory' },
  { to: '/returns', icon: RotateCcw, label: 'Returns' },
  { to: '/tickets', icon: Package, label: 'Tickets' },
  { to: '/profile', icon: User, label: 'My Store' },
];

interface SidebarProps {
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

export default function Sidebar({ mobileOpen, setMobileOpen }: SidebarProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { seller: authSeller } = useSelector((s: RootState) => s.auth);
    const [seller, setSeller] = useState(authSeller);
useEffect(() => {
    sellerAPI.getProfile().then(res => {
      const s = res.data.seller;
      setSeller(s);
    })
  }, []);
  const handleLogout = () => {
    Swal.fire({
      title: 'Ready to leave?',
      text: "You are about to securely log out of your session.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        authAPI.logout().catch(() => {});
        localStorage.clear();
        dispatch(logout());
        navigate('/login');
      }
    });
  };

  return (
    <>
      {/* Mobile overlay - click to close */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 39,
            animation: 'fadeIn 0.2s ease',
          }}
        />
      )}

      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        {/* Logo + Close button */}
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor:"pointer" }}
          onClick={()=> {
            navigate("/dashboard")
          }}
          >
            <div style={{
              width: '38px', height: '38px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Stethoscope size={20} color="white" />
            </div>
            <div>
              <div style={{ fontWeight: '800', fontSize: '17px', color: 'white', letterSpacing: '-0.3px' }}>
                Decso<span style={{ color: '#14b8a6' }}>plus</span>
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Seller Portal</div>
            </div>
          </div>
          {/* Close button - visible only on mobile */}
          <button
            className="sidebar-close-btn"
            onClick={() => setMobileOpen(false)}
            style={{
              display: 'none',
              width: '32px', height: '32px', borderRadius: '8px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.1)',
              cursor: 'pointer',
              alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s ease',
              color: '#94a3b8', fontSize: '18px', fontWeight: '600',
            }}
            title="Close menu"
          >
            ✕
          </button>
        </div>

        {/* Seller info */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className='profile-header-card' style={{
              width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', fontWeight: '700', color: 'white',
            }}>
              {/* <img src={seller?.storeLogo} style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> */}
              {seller?.storeLogo ? (
              <img src={seller?.storeLogo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            ) : (
              <span style={{ color: 'white', fontWeight: '800', fontSize: '18px' }}>
                {(seller?.storeinfo?.store_name || seller?.businessName || seller?.contactPerson || 'S')?.[0]?.toUpperCase()}
              </span>
            )}
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ color: 'white', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={seller?.storeinfo?.store_name || seller?.businessName || seller?.storeName || 'My Store'}>
                {seller?.storeinfo?.store_name || seller?.businessName || seller?.storeName || 'My Store'}
              </div>
              <div style={{ color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {seller?.email}
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="hide-scrollbar" style={{ padding: '0 12px', flex: 1, overflowY: 'auto' }}>
          <div style={{ fontSize: '10px', color: '#475569', fontWeight: '700', padding: '8px 4px 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Main Menu
          </div>
          {navItems.filter(item => {
            if (seller?.isBlock || seller?.verification_status === "P" || seller?.verification_status === "R") {
              return item.to === '/dashboard' || item.to === '/profile';
            }
            return true;
          }).map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen && setMobileOpen(false)}
            >
              <Icon size={18} />
              <span style={{ flex: 1 }}>{label}</span>
            </NavLink>
          ))}
        </nav>

       

        {/* Logout */}
        <div style={{ padding: '1px 2px' }}>
          <button
            onClick={handleLogout}
            className="nav-item"
            style={{ width: '100%', border: 'none', cursor: 'pointer', background: 'transparent', color: '#ef4444' }}
          > 
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
         <div style={{ padding: '1px' }}>
          <span className="nav-item" style={{ display: "flex", alignItems: "center", color: '#64748b', fontSize: "12px"}}>
            Version : 1.8.2
          </span>
        </div>
      </aside>
    </>
  );
}
